import {
  RoiRequestLifecycleState,
  RoiRequestState,
} from "./roi-request-create.types";

export interface IDemoPersistedRoiRequest extends RoiRequestState {
  created_at: string;
  saved_at: string;
  mrn?: string;
  lda_id?: string;
  created_by?: string;
  completed_by?: string;
  cancelled_by?: string;
  cancelled_at?: string;
}

export interface IDemoRoiRequestListRow extends Record<string, unknown> {
  mrn: string;
  ldaId: string;
  createdBy: string;
  dateCreated: string;
  lastUpdated: string;
  completedBy: string;
  dateCompleted: string;
  cancelledBy: string;
  dateCancelled: string;
  recordsCount: string;
  requestId: string;
  roiStatus: "Draft" | "Completed" | "Cancelled";
}

export interface IDemoRoiRequestDetails extends Record<string, unknown> {
  mrn: string;
  ldaId: string;
  createdBy: string;
  dateCreated: string;
  lastUpdated: string;
  completedBy: string;
  dateCompleted: string;
  cancelledBy: string;
  dateCancelled: string;
  recordsCount: string;
  requestId: string;
  roiStatus: "Draft" | "Completed" | "Cancelled";
}

const DEMO_ROI_REQUESTS_STORAGE_KEY = "demo-roi-requests";
const DEMO_USER_NAME = "Demo User";
const DEMO_MRN = "100502";
const DEMO_LDA_ID = "459";

export const formatDemoRoiRequestDateTime = (isoDate: string) =>
  new Intl.DateTimeFormat("en-US", {
    month: "2-digit",
    day: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  }).format(new Date(isoDate));

const mapLifecycleStateToStatus = (
  state: RoiRequestLifecycleState,
): "Draft" | "Completed" | "Cancelled" => {
  switch (state) {
    case "COMPLETED":
      return "Completed";
    case "CANCELLED":
      return "Cancelled";
    default:
      return "Draft";
  }
};

const getRequestMrn = (request: IDemoPersistedRoiRequest) =>
  request.mrn ?? DEMO_MRN;

const getRequestLdaId = (request: IDemoPersistedRoiRequest) =>
  request.lda_id ?? DEMO_LDA_ID;

const getRequestCreatedBy = (request: IDemoPersistedRoiRequest) =>
  request.created_by ?? DEMO_USER_NAME;

const getRequestCompletedBy = (request: IDemoPersistedRoiRequest) =>
  request.completed_by ?? getRequestCreatedBy(request);

const getRequestCancelledBy = (request: IDemoPersistedRoiRequest) =>
  request.cancelled_by ?? DEMO_USER_NAME;

const sortRequestsByLastUpdateDesc = (
  leftRequest: IDemoPersistedRoiRequest,
  rightRequest: IDemoPersistedRoiRequest,
) =>
  new Date(rightRequest.saved_at).getTime() -
  new Date(leftRequest.saved_at).getTime();

const cloneRequestSelections = (
  selections: RoiRequestState["data"],
): RoiRequestState["data"] =>
  selections.map((section) => ({
    ...section,
    selection: [...section.selection],
  }));

export const getDemoStoredRoiRequests = (): IDemoPersistedRoiRequest[] => {
  try {
    const rawValue = window.localStorage.getItem(DEMO_ROI_REQUESTS_STORAGE_KEY);

    if (!rawValue) {
      return [];
    }

    const parsedValue = JSON.parse(rawValue) as IDemoPersistedRoiRequest[];

    return Array.isArray(parsedValue) ? parsedValue : [];
  } catch {
    return [];
  }
};

export const mergeDemoRoiRequests = (
  ...requestCollections: IDemoPersistedRoiRequest[][]
): IDemoPersistedRoiRequest[] => {
  const requestMap = new Map<number, IDemoPersistedRoiRequest>();

  requestCollections.flat().forEach((request) => {
    requestMap.set(request.request_id, request);
  });

  return Array.from(requestMap.values()).sort(sortRequestsByLastUpdateDesc);
};

export const getDemoRoiRequestById = ({
  requestId,
  fallbackRequests = [],
}: {
  requestId: number;
  fallbackRequests?: IDemoPersistedRoiRequest[];
}) =>
  mergeDemoRoiRequests(fallbackRequests, getDemoStoredRoiRequests()).find(
    (request) => request.request_id === requestId,
  );

export const isDemoRoiRequestIdUnique = ({
  requestId,
  fallbackRequests = [],
}: {
  requestId: number;
  fallbackRequests?: IDemoPersistedRoiRequest[];
}) =>
  !mergeDemoRoiRequests(fallbackRequests, getDemoStoredRoiRequests()).some(
    (request) => request.request_id === requestId,
  );

export const saveDemoRoiRequest = (
  request: RoiRequestState,
  options?: {
    baseRequest?: Partial<IDemoPersistedRoiRequest>;
  },
): IDemoPersistedRoiRequest => {
  const nowIsoString = new Date().toISOString();
  const existingRequests = getDemoStoredRoiRequests();
  const storedRequest = existingRequests.find(
    (existingRequest) => existingRequest.request_id === request.request_id,
  );
  const baseRequest = storedRequest ?? options?.baseRequest;

  const nextRequest: IDemoPersistedRoiRequest = {
    ...request,
    created_at: baseRequest?.created_at ?? nowIsoString,
    saved_at: nowIsoString,
    mrn: baseRequest?.mrn ?? DEMO_MRN,
    lda_id: baseRequest?.lda_id ?? DEMO_LDA_ID,
    created_by: baseRequest?.created_by ?? DEMO_USER_NAME,
    completed_by:
      request.state === "COMPLETED"
        ? storedRequest?.completed_by ??
          options?.baseRequest?.completed_by ??
          DEMO_USER_NAME
        : undefined,
    cancelled_by:
      request.state === "CANCELLED"
        ? storedRequest?.cancelled_by ??
          options?.baseRequest?.cancelled_by ??
          DEMO_USER_NAME
        : undefined,
    cancelled_at:
      request.state === "CANCELLED" ? nowIsoString : undefined,
  };

  const nextRequests = [
    nextRequest,
    ...existingRequests.filter(
      (storedRequest) => storedRequest.request_id !== request.request_id,
    ),
  ];

  // Demo only: keep locally created ROI requests in localStorage because the app
  // currently runs entirely on mocks and has no backend persistence.
  window.localStorage.setItem(
    DEMO_ROI_REQUESTS_STORAGE_KEY,
    JSON.stringify(nextRequests),
  );

  return nextRequest;
};

export const duplicateDemoRoiRequest = ({
  sourceRequest,
  requestId,
  fallbackRequests = [],
}: {
  sourceRequest: IDemoPersistedRoiRequest;
  requestId: number;
  fallbackRequests?: IDemoPersistedRoiRequest[];
}): IDemoPersistedRoiRequest | null => {
  if (sourceRequest.state !== "COMPLETED") {
    return null;
  }

  if (!Number.isFinite(requestId) || requestId <= 0) {
    return null;
  }

  if (!isDemoRoiRequestIdUnique({ requestId, fallbackRequests })) {
    return null;
  }

  return saveDemoRoiRequest(
    {
      request_id: requestId,
      state: "DRAFT",
      total_selected_items_count: sourceRequest.total_selected_items_count,
      data: cloneRequestSelections(sourceRequest.data),
    },
    {
      baseRequest: {
        mrn: sourceRequest.mrn,
        lda_id: sourceRequest.lda_id,
      },
    },
  );
};

export const mapDemoStoredRequestToDetails = (
  request: IDemoPersistedRoiRequest,
): IDemoRoiRequestDetails => {
  const isCompleted = request.state === "COMPLETED";
  const isCancelled = request.state === "CANCELLED";

  return {
    mrn: getRequestMrn(request),
    ldaId: getRequestLdaId(request),
    createdBy: getRequestCreatedBy(request),
    dateCreated: formatDemoRoiRequestDateTime(request.created_at),
    lastUpdated: formatDemoRoiRequestDateTime(request.saved_at),
    completedBy: isCompleted ? getRequestCompletedBy(request) : "—",
    dateCompleted: isCompleted
      ? formatDemoRoiRequestDateTime(request.saved_at)
      : "—",
    cancelledBy: isCancelled ? getRequestCancelledBy(request) : "—",
    dateCancelled:
      isCancelled && request.cancelled_at
        ? formatDemoRoiRequestDateTime(request.cancelled_at)
        : "—",
    recordsCount: String(request.total_selected_items_count),
    requestId: String(request.request_id),
    roiStatus: mapLifecycleStateToStatus(request.state),
  };
};

export const mapDemoStoredRequestToRow = (
  request: IDemoPersistedRoiRequest,
): IDemoRoiRequestListRow => mapDemoStoredRequestToDetails(request);

export const mapDemoRequestsToRows = (
  requests: IDemoPersistedRoiRequest[],
): IDemoRoiRequestListRow[] => requests.map(mapDemoStoredRequestToRow);
